import styled from "@emotion/styled";
import rem from "../../utils/rem/rem";
import LinearProgress, { linearProgressClasses } from '@mui/material/LinearProgress';
import Link from "../../components/Link/Link";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

export const MoaDashboardHeader = styled.div`
  padding: ${rem(48)} ${rem(32)};
  background-color: ${({ theme }) => theme.colors.gray800};
  color: ${({ theme }) => theme.colors.white};
  font-family: 'Pizza Hut', serif;
  flex-direction: row;
  align-items: flex-end;
  height: 100px;
  display: flex;
  @media ${({ theme }) => theme.breakpoints.sm} {
    flex-direction: row;
  }
`;

export const DashboardBody = styled.div`
    width: 100%;
    min-height: 100%;
    display: flex;
    flex-flow: row nowrap;
    overflow: auto;
`;

export const SideContainers = styled.div`
  background-color: ${({ theme }) => theme.colors.gray100};
  margin-left: 32px;
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  flex-grow: 0.5;
  height: fit-content;
`;

export const H1 = styled.h1`
  font-size: ${rem(28)};
  margin-bottom: ${rem(40)};
`;

export const H2 = styled.h2`
  font-size: ${rem(14)};
  padding: ${rem(1)}
  font-weight: bold;
  font-family: 'Open Sans';
`;

export const HeaderWrapper = styled.div`
    margin-left: ${rem(2.5)};
    padding-right: 40px;
    justify-content: center;
    align-items: center;
`;

export const DropdownWrapperMedium = styled.div`
  padding: ${rem(48)} ${rem(2.5)} ${rem(32)};
  width: ${rem(190)};
`;

export const Container = styled.div`
    display: flex;
    flex-flow: column nowrap;
`

export const Row = styled.div`
    display: flex;
    flex-flow: row nowrap;
`

export const JustifiedRow = styled(Row)`
    justify-content: space-between;
`

export const ReverseJustifiedRow = styled(JustifiedRow)`
    flex-flow: row-reverse nowrap;
`

export const BatchStatusRow = styled(JustifiedRow)`
    padding-left: ${rem(8)}
`

export const Text = styled.h3`
    font-family: 'Open Sans', sans-serif;
    font-size: ${rem(14)};
    font-weight: normal;
    color: ${({ theme }) => theme.colors.gray900};
`

export const StyledTitle = styled(Text)`
    color: ${({ theme }) => theme.colors.gray600};
    margin-bottom: ${rem(8)};
`;

export const ProgressBarTitle = styled(StyledTitle)`
  padding: 0 ${rem(16)};
`

export const ExtractText = styled(Text)`
    flex: 1
`

export const BoldExtractText = styled(ExtractText)`
    font-weight: bold;
`

export const List = styled.ul`
    display: flex;
    flex-flow: column nowrap;
`

export const ScrollableList = styled(List)`
  overflow: auto;
`;

export const ExtractMetricsContainer = styled(Container)`
    padding: ${rem(8)};
    margin: ${rem(8)};
`

export const ExtractJobStatusList = styled(ScrollableList)`
    height: ${rem(144)};
    padding: 0 ${rem(16)};
    gap: ${rem(16)};
`

export const ExtractJobStatusRow = styled(JustifiedRow)`
    height: min-content;
    align-items: end;
`

export const ExtractJobStatusCell = styled.div`
    display: flex;
    flex-flow: row nowrap;
    align-items: end;
    flex: 1;
`

export const DashboardMainPanel = styled.div`
    padding: ${rem(16)};
    height: 100%;
    flex-grow: 2;
`;

export const DashboardRow = styled(Row)`
    width: 100%;
`;

export const DashboardContainer = styled(Container)`
    width: 100%;
    min-height: 150px;
    border: 1px solid ${({ theme }) => theme.colors.gray400};
    border-radius: ${rem(8)};
    margin: ${rem(16)};
    overflow: hidden;
`;

export const DashboardContainerHeader = styled.div`
  padding: ${rem(16)};
  font-family: 'Pizza Hut', serif;
  font-weight: 400;
  font-size: ${rem(26)};
  color: ${({ theme }) => theme.colors.gray900};
  background-color: ${({ theme }) => theme.colors.gray100};
`;

export const DashboardContainerBody = styled.div`
    padding: ${rem(16)};
    background-color: ${({ theme }) => theme.colors.white};
    flex: 1;
    display: flex;
    flex-flow: column nowrap;
    gap: ${rem(16)};
`;

export const ContainerContent = styled.ul`
  padding-bottom: ${rem(16)};
  display: flex;
  flex-wrap: nowrap;
`;

export const PublishedCountRow = styled(Row)`
    align-items: center;
    gap: ${rem(32)};
    margin: auto 0;
`;

export const DashboardFilterSelectContainer = styled.div`
    display: flex;
    flex-flow: row nowrap;
    gap: 5px;
`

export const TimePickerContainer = styled.div`
    padding: ${rem(16)} 0;
`;

export const EventsAmount = styled(Text)`
  font-weight: bold;
  font-size: ${rem(22)};
`;

export const EventsSubtitle = styled(Text)`
  font-size: ${rem(16)};
`;

export const ProgressBarContainer = styled(Container)`
    width: 60%;
`

export const ProgressBarRow = styled(Row)`
    position: relative;
    margin-left: ${rem(-8)};
`

export const ProgressText = styled(H2)`
    z-index: 10;
    font-weight: 600;
`

export const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 20,
  borderRadius: 10,
  width: '100%',
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: '#f7f7f7',
    border: 0.5,
    borderStyle: 'solid',
    borderColor: '#828282'
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: '#ffd37e',
  },
}));

export const ResolvedText = styled.span`
  color: green;
  font-family: 'Open Sans', sans-serif;
  font-weight: 400;
`;

export const FailedAlertContainer = styled.span`
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    align-self: flex-end;
`

export const FailedIcon = styled(ErrorOutlineIcon)`
    color: red;
`

export const SideDrawerLink = styled(Link)`
    width: max-content;
    padding:${rem(4)};
    color: ${({ theme }) => theme.colors.darkBlue};
    align-self: flex-end;
`